import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// httpOnly cookies handle auth (samesite=none; secure). No token in localStorage.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Single in-flight refresh promise to avoid a thundering herd of /auth/refresh
// calls when multiple requests receive 401 at the same time.
let refreshInFlight = null;

async function performRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
      .then((r) => r.data)
      .finally(() => {
        // Clear the in-flight marker after the promise settles so future 401s
        // can trigger a new refresh cycle if needed.
        setTimeout(() => { refreshInFlight = null; }, 0);
      });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const url = cfg.url || "";
    const isAuthEndpoint =
      url.includes("/auth/me") ||
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");
    const alreadyOnLogin =
      typeof window !== "undefined" && window.location.pathname === "/login";

    // On 401 for a normal API call, try silent refresh once, then replay.
    if (status === 401 && !isAuthEndpoint && !cfg._retriedAfterRefresh) {
      cfg._retriedAfterRefresh = true;
      try {
        await performRefresh();
        return api.request(cfg);
      } catch {
        if (!alreadyOnLogin) {
          window.location.href = "/login";
        }
        return Promise.reject(err);
      }
    }

    // Fallback: /auth/me or refresh itself failed → send user to login.
    if (status === 401 && !isAuthEndpoint && !alreadyOnLogin) {
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Errore imprevisto";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
}
